// ADR 6.9.26c §2 — the two ends of the render, and why they are two.
//
// A React error boundary cannot catch a rejection in the async function that
// mounts React, and a `.catch` on that function cannot catch a component that
// throws three routes later. So there are two mechanisms for two distinct
// failures, both landing in the same place:
//
//   bootstrap()  ──try/catch──▶  captureException + reveal #fatal into #root
//       │                        (initI18n rejected, #root missing, SDK threw)
//       └─▶ <RootErrorBoundary> ──componentDidCatch──▶ captureException + fallback UI
//               └─▶ <RouterProvider>
//
// Neither path calls `t()`. The failure `reportFatal` exists for is i18n never
// having initialised, so a localised fallback is a fallback that throws — and
// the copy lives in `index.html`'s `<template id="fatal">` rather than here,
// where it would be uncatalogued strings inside a `.tsx` file.

import { Component, type ErrorInfo, type ReactNode } from 'react'

import { captureException } from '../telemetry/capture'
import { revealFatal } from './revealFatal'

/**
 * The one handler `bootstrap()` is allowed to end in: report if Sentry was
 * constructed, and — either way — reveal something.
 *
 * `captureException` is a no-op with no Sentry instance (`capture.ts`), so a
 * visitor who denied crash reporting still gets the fallback and sends nothing.
 */
export function reportFatal(error: unknown): void {
  captureException(error)
  revealFatal()
}

interface BoundaryProps {
  children: ReactNode
}

interface BoundaryState {
  failed: boolean
}

/**
 * The render-time half. Reports **once** per failure: `componentDidCatch` is
 * where the report goes, not `getDerivedStateFromError`, which React may call
 * more than once for one error (and does, twice, under StrictMode in
 * development).
 */
export class RootErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { failed: false }

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error)
    // The component stack is the whole value of `info` here and Sentry's React
    // integration reads it off the boundary itself; logging it keeps the stack
    // visible to anyone debugging with telemetry denied.
    console.error('render failed', info.componentStack)
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children
    return (
      <div role="alert" className="mx-auto mt-16 max-w-lg px-6 text-center">
        <h1 className="text-xl font-semibold">Lingara could not start</h1>
        <p className="mt-3 leading-relaxed">
          Something went wrong. Reloading the page usually fixes it.
        </p>
      </div>
    )
  }
}
